<script setup lang="ts">
import type { FormDataConvertible } from '@inertiajs/core'
import { Head, router } from '@inertiajs/vue3'
import { ChevronDown, ChevronLeft, ChevronRight, Download, FileBarChart, Loader2, Save, X } from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppDialog from '@/components/AppDialog.vue'
import ReportColumnPicker from '@/components/reports/ReportColumnPicker.vue'
import ReportExportDialog from '@/components/reports/ReportExportDialog.vue'
import ReportRuleGroup from '@/components/reports/ReportRuleGroup.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import AppLayout from '@/layouts/AppLayout.vue'
import { apiApiReportsPreviewPath, apiApiReportsRegistryPath, reportPath, reportsPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import {
  type BaseEntityOption,
  type RegistryEntity,
  type ReportSort,
  type RuleGroup,
  type SavedReportDetail,
  emptyGroup,
  hasCountFields,
  pruneRuleTree,
  serializeRuleTree,
} from '@/types/reports'

const props = defineProps<{ report: SavedReportDetail | null; base_entities: BaseEntityOption[] }>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Reports', href: reportsPath() },
  { title: props.report ? props.report.name : 'New Report', href: '#' },
]

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

const baseEntity = ref<string | null>(props.report?.base_entity ?? null)
const registryEntity = ref<RegistryEntity | null>(null)
const ruleTree = ref<RuleGroup>(props.report?.rule_tree ?? emptyGroup())
const columns = ref<string[]>(props.report?.columns ?? [])
const sort = ref<ReportSort | null>(props.report?.sort ?? null)
const columnsOpen = ref(false)
const countDateFrom = ref(props.report?.count_date_range?.from ?? '')
const countDateTo = ref(props.report?.count_date_range?.to ?? '')

const countDateRange = computed(() =>
  countDateFrom.value && countDateTo.value ? { from: countDateFrom.value, to: countDateTo.value } : null,
)

let initializing = true

async function loadRegistry(entity: string) {
  const res = await fetch(`${apiApiReportsRegistryPath()}?base_entity=${entity}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  registryEntity.value = (await res.json()) as RegistryEntity
}

watch(
  baseEntity,
  async (next, previous) => {
    if (!next) {
      registryEntity.value = null
      return
    }

    const isInitialLoad = initializing

    if (!initializing && previous) {
      ruleTree.value = emptyGroup()
      columns.value = []
      sort.value = null
    }
    initializing = false

    await loadRegistry(next)

    if (columns.value.length === 0 && !(isInitialLoad && props.report)) {
      columns.value = registryEntity.value?.default_columns ?? []
    }
  },
  { immediate: true },
)

interface PreviewRow {
  [key: string]: unknown
}

const previewRows = ref<PreviewRow[]>([])
const previewPagination = ref({ current_page: 1, total_pages: 1, total_count: 0, per_page: 20 })
const previewLoading = ref(false)
const previewError = ref<string | null>(null)
const page = ref(1)

let previewRequestSeq = 0

async function runPreview() {
  if (!baseEntity.value) return

  const requestSeq = ++previewRequestSeq
  previewLoading.value = true
  previewError.value = null

  try {
    const res = await fetch(apiApiReportsPreviewPath(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({
        base_entity: baseEntity.value,
        rule_tree: serializeRuleTree(pruneRuleTree(ruleTree.value)),
        columns: columns.value,
        sort: sort.value,
        count_date_range: countDateRange.value,
        page: page.value,
      }),
    })

    const data = await res.json()
    if (requestSeq !== previewRequestSeq) return // a newer request has since started — discard

    previewLoading.value = false

    if (!res.ok) {
      previewError.value = data.error ?? 'Failed to run preview.'
      previewRows.value = []
      return
    }

    previewRows.value = data.rows
    previewPagination.value = data.pagination
  } catch {
    if (requestSeq !== previewRequestSeq) return
    previewLoading.value = false
    previewError.value = 'Failed to run preview — check your connection and try again.'
    previewRows.value = []
  }
}

function goToPage(next: number) {
  page.value = next
  runPreview()
}

let debounceTimer: ReturnType<typeof setTimeout>
watch(
  [ruleTree, columns, sort, baseEntity, countDateFrom, countDateTo],
  () => {
    page.value = 1
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(runPreview, 400)
  },
  { deep: true, immediate: true },
)

onUnmounted(() => clearTimeout(debounceTimer))

const previewColumns = computed<TableColumn[]>(() => {
  const paths = columns.value.length > 0 ? columns.value : Object.keys(previewRows.value[0] ?? {})
  return paths.map((path) => ({ key: path, label: fieldLabel(path), headerClass: 'whitespace-nowrap' }))
})

function fieldLabel(path: string): string {
  if (!registryEntity.value) return path
  const [relKey, fieldKey] = path.includes('.') ? path.split('.', 2) : [null, path]
  if (!relKey) return registryEntity.value.fields.find((f) => f.key === fieldKey)?.label ?? path
  const rel = registryEntity.value.relations[relKey]
  return rel ? `${rel.label}: ${rel.fields.find((f) => f.key === fieldKey)?.label ?? fieldKey}` : path
}

const showSaveDialog = ref(false)
const saveName = ref(props.report?.name ?? '')
const saving = ref(false)

function openSaveDialog() {
  saveName.value = props.report?.name ?? ''
  showSaveDialog.value = true
}

function submitSave() {
  if (!baseEntity.value) return
  saving.value = true

  const payload = {
    name: saveName.value,
    base_entity: baseEntity.value,
    rule_tree: serializeRuleTree(pruneRuleTree(ruleTree.value)),
    columns: columns.value,
    sort: sort.value,
    count_date_range: countDateRange.value,
  }

  const options = {
    preserveScroll: true,
    onSuccess: () => {
      showSaveDialog.value = false
    },
    onFinish: () => {
      saving.value = false
    },
  }

  if (props.report) {
    router.patch(reportPath(props.report.slug), payload as unknown as Record<string, FormDataConvertible>, options)
  } else {
    router.post(reportsPath(), payload as unknown as Record<string, FormDataConvertible>, options)
  }
}

const exportDialog = ref<InstanceType<typeof ReportExportDialog> | null>(null)

function startExport() {
  if (!baseEntity.value) return
  exportDialog.value?.start({
    base_entity: baseEntity.value,
    rule_tree: serializeRuleTree(pruneRuleTree(ruleTree.value)),
    columns: columns.value,
    sort: sort.value,
    count_date_range: countDateRange.value,
  })
}
</script>

<template>
  <Head :title="report ? report.name : 'New Report'" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <FileBarChart class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">{{ report ? report.name : 'New Report' }}</h1>
            <p class="text-muted-foreground text-sm">Build a filtered report and export it to CSV</p>
          </div>
        </div>
        <div class="flex gap-2">
          <Button variant="outline" :disabled="!baseEntity" @click="openSaveDialog">
            <Save class="size-4" />
            Save
          </Button>
          <Button :disabled="!baseEntity" @click="startExport">
            <Download class="size-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="flex flex-wrap items-center justify-between gap-3 pb-3">
          <div class="flex flex-wrap items-center gap-3">
            <CardTitle class="text-base">Filters</CardTitle>
            <div class="flex items-center gap-2">
              <Label class="text-muted-foreground text-sm font-medium">Base entity</Label>
              <Select :model-value="baseEntity ?? undefined" @update:model-value="(v) => (baseEntity = v as string)">
                <SelectTrigger class="h-8 w-56 text-sm">
                  <SelectValue placeholder="Choose what to report on…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="e in base_entities" :key="e.key" :value="e.key">{{ e.label }}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div v-if="registryEntity" class="flex items-center gap-2">
              <span class="text-muted-foreground text-xs font-medium">Match</span>
              <Select
                :model-value="ruleTree.combinator"
                @update:model-value="(v) => (ruleTree = { ...ruleTree, combinator: v as 'and' | 'or' })"
              >
                <SelectTrigger class="h-7 w-28 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="and">all (AND)</SelectItem>
                  <SelectItem value="or">any (OR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            v-if="ruleTree.conditions.length > 0"
            type="button"
            variant="ghost"
            size="sm"
            @click="ruleTree = emptyGroup()"
          >
            <X class="size-3.5" />
            Clear filters
          </Button>
        </CardHeader>
        <CardContent class="space-y-4">
          <ReportRuleGroup
            v-if="registryEntity"
            :group="ruleTree"
            :entity="registryEntity"
            hide-match-header
            is-root
            @update:group="(v) => (ruleTree = v)"
          />
        </CardContent>
      </Card>

      <template v-if="registryEntity">
        <Card class="border-sidebar-border/70 dark:border-sidebar-border">
          <Collapsible :open="columnsOpen" @update:open="(v) => (columnsOpen = v)">
            <CardHeader class="pb-3">
              <CollapsibleTrigger as-child>
                <button type="button" class="flex w-full items-center justify-between gap-2 text-left">
                  <CardTitle class="flex items-center gap-1.5 text-base">
                    <ChevronDown
                      class="text-muted-foreground size-4 transition-transform"
                      :class="{ '-rotate-90': !columnsOpen }"
                    />
                    Columns & sort
                  </CardTitle>
                  <span class="text-muted-foreground text-sm">
                    {{
                      columns.length === 0
                        ? 'All columns'
                        : `${columns.length} column${columns.length !== 1 ? 's' : ''}`
                    }}
                  </span>
                </button>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent class="space-y-4">
                <div v-if="hasCountFields(registryEntity)" class="border-border space-y-1.5 border-b pb-4">
                  <Label class="text-sm font-medium">Count date range</Label>
                  <p class="text-muted-foreground text-xs">
                    Scopes count columns (e.g. Application Total) to activity within this range. Leave blank to count
                    all-time.
                  </p>
                  <div class="flex w-full flex-col items-center gap-1.5 sm:flex-row">
                    <AppDatePicker
                      v-model="countDateFrom"
                      :max="countDateTo || undefined"
                      placeholder="From"
                      class="w-auto min-w-40"
                    />
                    <span class="text-muted-foreground hidden text-xs select-none sm:block">–</span>
                    <AppDatePicker
                      v-model="countDateTo"
                      :min="countDateFrom || undefined"
                      placeholder="To"
                      class="w-auto min-w-40"
                    />
                  </div>
                </div>

                <ReportColumnPicker
                  :columns="columns"
                  :entity="registryEntity"
                  :sort="sort"
                  @update:columns="(v) => (columns = v)"
                  @update:sort="(v) => (sort = v)"
                />
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        <Card class="border-sidebar-border/70 dark:border-sidebar-border">
          <CardHeader class="flex items-center gap-1.5 pb-3">
            <CardTitle class="text-base">Preview</CardTitle>
            <span v-if="!previewError" class="text-muted-foreground text-sm">
              {{ previewPagination.total_count.toLocaleString() }} result found
            </span>
          </CardHeader>
          <CardContent class="p-0">
            <p v-if="previewError" class="text-destructive px-6 py-4 text-sm">{{ previewError }}</p>
            <div
              v-else-if="previewLoading && previewRows.length === 0"
              class="text-muted-foreground flex items-center justify-center gap-2 px-6 py-12 text-sm"
            >
              <Loader2 class="size-4 animate-spin" />
              Loading preview…
            </div>
            <p v-else-if="previewRows.length === 0" class="text-muted-foreground px-6 py-8 text-center text-sm">
              No matching records.
            </p>
            <div v-else class="relative">
              <AppDataTable :columns="previewColumns">
                <tr v-for="(row, i) in previewRows" :key="i" class="border-border border-b last:border-0">
                  <td
                    v-for="col in previewColumns"
                    :key="col.key"
                    class="text-muted-foreground w-px px-6 py-3 whitespace-nowrap"
                  >
                    {{ row[col.key] ?? '—' }}
                  </td>
                </tr>
              </AppDataTable>
              <Transition
                enter-active-class="transition-opacity duration-150"
                leave-active-class="transition-opacity duration-150"
                enter-from-class="opacity-0"
                leave-to-class="opacity-0"
              >
                <div
                  v-if="previewLoading"
                  class="bg-background/60 absolute inset-0 flex items-start justify-center pt-12"
                >
                  <span class="text-muted-foreground flex items-center gap-2 text-sm">
                    <Loader2 class="size-4 animate-spin" />
                    Updating…
                  </span>
                </div>
              </Transition>
            </div>
            <div v-if="previewPagination.total_pages > 1" class="flex items-center justify-between gap-4 px-6 py-3">
              <p class="text-muted-foreground text-sm">
                Page {{ previewPagination.current_page }} of {{ previewPagination.total_pages }}
              </p>
              <div class="flex gap-2">
                <Button variant="outline" size="sm" :disabled="page <= 1" @click="goToPage(page - 1)">
                  <ChevronLeft class="size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  :disabled="page >= previewPagination.total_pages"
                  @click="goToPage(page + 1)"
                >
                  Next
                  <ChevronRight class="size-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </template>
    </div>

    <AppDialog
      v-model:open="showSaveDialog"
      title="Save report"
      description="Give this report a name so you can reuse it later."
    >
      <div class="grid gap-2">
        <Label for="report-name">Name</Label>
        <Input id="report-name" v-model="saveName" placeholder="Settled loans by partner" />
      </div>
      <template #footer>
        <Button variant="outline" @click="showSaveDialog = false">Cancel</Button>
        <Button :disabled="saving || !saveName" @click="submitSave">Save</Button>
      </template>
    </AppDialog>

    <ReportExportDialog ref="exportDialog" />
  </AppLayout>
</template>
